import React, { useEffect, useState } from "react";
import styles from "./add-edit-exam-modal.module.css";
import { Col, Modal, Row, Select, Spin } from "antd";
import { ClockCircleOutlined, CloseOutlined } from "@ant-design/icons";
import FormFooter from "../../../../components/form-footer/form-footer";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import CustomSelect from "../../../../common/input/custom-select/custom-select";
import CustomTimePicker from "../../../../common/input/custom-timepicker/custom-timepicker";
import CustomTextfield from "../../../../common/input/custom-textfield/custom-textfield";
import { fetchSubjects } from "../../../../services/student";
import { createAntdSelection } from "../../../../utils/common";
import {
  getAllSectionByLevel,
  getExamTeachers,
  getSingleExamInfo,
  setExam,
  getRooms,
  getLevelsBySubjects,
  updateExam,
} from "../../../../services/examination";
import CustomCheckBox from "../../../../common/custom-checkbox/custom-checkbox";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { toast } from "react-toastify";
import { getTeachersList } from "../../../../services/teachers";

dayjs.extend(customParseFormat);

const classRooms = Array.from({ length: 8 }, (_, idx) => ({
  label: `Paper ${idx + 1}`,
  value: `Paper ${idx + 1}`,
}));

const AddEditExamModal = ({
  open,
  onClose,
  examContext,
  fetchAndRenderTable,
}) => {
  const { control, getValues, reset, watch, trigger, resetField } = useForm({
    mode: "onChange",
    defaultValues: {
      subject: "",
      paper: "",
      level: "",
      start_time: "",
      end_time: "",
      maximum_marks: "",
      minimum_marks: "",
      weightage: "",
    },
  });

  // setting up all the subjects, levels, sections and teachers
  const [loading, setLoading] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [levels, setLevels] = useState([]);
  const [sections, setSections] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [allTeachers, setAllTeachers] = useState([]);
  const [rooms, setRooms] = useState([]);

  const [sectionsInput, setSectionsInput] = useState([]);

  // watching for the these values as need to react based on these
  const startTime = watch("start_time");
  const endTime = watch("end_time");
  const subject = watch("subject");
  const level = watch("level");

  // fetching teachers and setting to state for dropdown
  const fetchExamTeachers = async (startTime, endTime) => {
    try {
      setLoading(true);

      const start_time = dayjs(startTime).format("HH:mm:ss");
      const end_time = dayjs(endTime).format("HH:mm:ss");
      const examination_date = dayjs(examContext.exam_date).format(
        "YYYY-MM-DD"
      );

      const isEdit = examContext.exam_info;
      let resp = null;

      const payload = {
        examination_date,
        end_time,
        start_time,
      };
      if (isEdit) {
        payload.isEdited = true;
        payload.setExamId = examContext?.exam_info?.id;
        resp = await getExamTeachers(payload);
      } else resp = await getExamTeachers(payload);
      setTeachers(createAntdSelection(resp?.data?.data?.teachers, "full_name"));
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  // fetching subjects and setting to state for dropdown
  const fetchSchoolSubjects = async () => {
    try {
      setLoading(true);
      const resp = await fetchSubjects();
      const data = resp?.data?.data?.subjects;
      setSubjects(createAntdSelection(data));
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  // fetching all teachers of the institute
  const fetchAllTeachers = async () => {
    try {
      setLoading(true);
      const resp = await getTeachersList({});
      setAllTeachers(createAntdSelection(resp?.data?.data?.teachers, "full_name"));
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  // fetching school rooms and setting to state for dropdown
  const fetchSchoolRooms = async (startTime, endTime) => {
    try {
      setLoading(true);
      const start_time = dayjs(startTime).format("HH:mm:ss");
      const end_time = dayjs(endTime).format("HH:mm:ss");
      const examination_date = dayjs(examContext?.exam_date).format(
        "YYYY-MM-DD"
      );

      const isEdit = !!examContext.exam_info;
      let resp = null;

      const payload = {
        start_time,
        end_time,
        examination_date,
      };

      if (isEdit) {
        payload.isEdit = true;
        payload.setExamId = examContext?.exam_info?.id;
        resp = await getRooms(payload);
      } else resp = await getRooms(payload);

      const data = resp?.data?.data?.rooms;
      setRooms(createAntdSelection(data, "room_number"));
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  // fetching sections by levels and setting to state for dropdown
  const fetchSectionByLevels = async (levelId) => {
    try {
      setLoading(true);
      const subject = getValues().subject;
      const resp = await getAllSectionByLevel(levelId, subject);
      const sectionsData = resp?.data?.data?.classRooms;
      setSections(sectionsData);
      const sectionData = sectionsData?.map((item) => {
        return {
          id: item?.id,
          name: item?.name,
          selected: false,
          selected_room: "",
          invigilator: "",
          marking_teacher: "",
        };
      });

      setSectionsInput(sectionData);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  // fetching levels and setting to state for dropdown
  const fetchLevels = async (subject_id) => {
    try {
      setLoading(true);
      const resp = await getLevelsBySubjects(subject_id);
      const levelsData = resp?.data?.data;
      setLevels(createAntdSelection(levelsData));
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSectionLevelsAndAppend = async (info) => {
    try {
      const levelId = info?.level?.level_id;
      const section_data = info?.sections_and_details;
      const subject_id = info?.subject_id;
      setLoading(true);
      const resp = await getAllSectionByLevel(levelId, subject_id);
      const sectionsData = resp?.data?.data?.classRooms;
      setSections(sectionsData);
      const data = sectionsData.map((item, idx) => {
        const selected = section_data?.find((i) => i.section_id === item.id);
        return {
          id: item?.id,
          name: item?.name,
          selected: !!selected,
          selected_room: selected?.room_details?.id ?? "",
          invigilator: selected?.teacher?.[0]?.teacher_id ?? "",
          marking_teacher: selected?.marking_teacher?.[0]?.teacher_id ?? "",
        };
      });
      setSectionsInput(data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchSchoolSubjects(); // fetch subjects
      fetchAllTeachers();
      const info = examContext.exam_info; // get current date data from the slot table
      if (info) {
        const obj = {
          subject: info.subject_id,
          paper: info.paper,
          level: info?.level?.level_id,
          start_time: dayjs(info?.start_time, "HH:mm:ss"),
          end_time: dayjs(info?.end_time, "HH:mm:ss"),
          maximum_marks: info?.max_marks,
          minimum_marks: info?.min_marks,
          weightage: info?.weightage,
        };
        reset(obj); // resetting for data
        fetchLevels(info.subject_id); // fetching levels
        fetchSectionLevelsAndAppend(info);
      } else {
        reset({
          subject: "",
          paper: "",
          level: "",
          start_time: "",
          end_time: "",
          maximum_marks: "",
          minimum_marks: "",
          weightage: "",
        });
        setSections([]);
        setLevels([]);
        setRooms([]);
        setTeachers([]);
        setSubjects([]);
        setSectionsInput([]);
      }
    }
  }, [open, examContext.exam_info, reset]);

  useEffect(() => {
    if (startTime && endTime) {
      fetchExamTeachers(startTime, endTime);
      fetchSchoolRooms(startTime, endTime);
    } else {
      setTeachers([]);
      setRooms([]);
      setSectionsInput((pre) => {
        return pre.map((i) => ({
          ...i,
          selected: false,
          selected_room: "",
          invigilator: "",
          marking_teacher: "",
        }));
      });
    }
  }, [startTime, endTime]);

  const handleSaveExam = async () => {
    try {
      const isValid = await trigger();
      if (!isValid) return;

      const teacherDuplicate = sectionsInput.flatMap((i) =>
        i?.invigilator?.length ? i.invigilator : []
      );
      const roomDuplicate = sectionsInput.flatMap((i) =>
        i?.selected_room?.length ? i.selected_room : []
      );

      if (new Set(teacherDuplicate).size !== teacherDuplicate.length)
        return toast.error("Duplicated Teacher is not allowed");
      if (new Set(roomDuplicate).size !== roomDuplicate.length)
        return toast.error("Duplicated Room is not allowed");

      setLoading(true);
      const formValue = getValues();
      const getSubName = subjects.find((i) => i.value === formValue.subject);
      const getLevel = levels.find((i) => i.value === formValue.level);

      const sectionInfo = sectionsInput?.flatMap((item) => {
        if (!item.selected) return [];
        const sectionName = sections.find((i) => i.id === item.id);
        const findTeacher = teachers.find((i) => i.value === item.invigilator);
        const findMarkingTeacher = allTeachers.find((i) => i.value === item.marking_teacher);
        const room = rooms.find((i) => i.value === item.selected_room);
        return {
          section_name: sectionName?.name,
          section_id: item?.id,
          room_id: room?.value || null,
          teacher: findTeacher
            ? [
              {
                teacher_name: findTeacher.label,
                teacher_id: item?.invigilator,
              },
            ]
            : [],
          marking_teacher: findMarkingTeacher
            ? [
              {
                teacher_name: findMarkingTeacher?.label,
                teacher_id: item?.marking_teacher,
              },
            ]
            : [],
        };
      });

      const payload = {
        subject_name: getSubName.label,
        subject_id: formValue.subject,
        paper: formValue.paper,
        exam_id: examContext.exam_id,
        id: examContext?.exam_info?.id,
        level: {
          level_id: formValue.level,
          level_name: getLevel.label,
        },
        sections_and_details: sectionInfo,
        examination_date: dayjs(examContext?.exam_date).format("YYYY-MM-DD"),
        start_time: dayjs(formValue.start_time).format("HH:mm"),
        end_time: dayjs(formValue.end_time).format("HH:mm"),
        max_marks: +formValue.maximum_marks,
        min_marks: +formValue.minimum_marks,
        weightage: +formValue.weightage,
      };
      if (payload.id) {
        await updateExam(payload);
      } else await setExam(payload);
      onClose();
      const resp = await getSingleExamInfo({ exam_id: examContext.exam_id });
      fetchAndRenderTable(resp);
      reset({
        subject: "",
        paper: "",
        level: "",
        start_time: "",
        end_time: "",
        maximum_marks: "",
        minimum_marks: "",
        weightage: "",
      });
      setSections([]);
      setLevels([]);
      setRooms([]);
      setTeachers([]);
      setSubjects([]);
    } catch (err) {
      toast.error(err?.data?.message || "Failed to set Exam");
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const disableCloseOnLoading = !loading ? onClose : null;

  const handleCheckbox = (id) => (event) => {
    const checked = event?.target?.checked;
    setSectionsInput((pre) => {
      return pre.map((i) => {
        if (i.id === id) {
          if (checked) {
            return {
              ...i,
              selected: checked,
            };
          } else {
            return {
              ...i,
              selected: checked,
              selected_room: "",
              invigilator: "",
              marking_teacher: ""
            };
          }
        }
        return i;
      });
    });
  };

  const handleChangeSections = (fieldName, id) => (event) => {
    setSectionsInput((pre) => {
      return pre.map((i) => {
        if (i.id === id)
          return {
            ...i,
            [fieldName]: event?.target?.checked ?? event,
          };
        return i;
      });
    });
  };

  return (
    <Modal
      onCancel={disableCloseOnLoading}
      wrapClassName={styles.modalWrap}
      styles={{
        content: { padding: 0, margin: 0 },
      }}
      closeIcon={null}
      width="720px"
      onClose={null}
      footer={null}
      open={open}
    >
      <Spin spinning={loading}>
        <div className={styles["add-edit-header-exam-header"]}>
          Set Exam For&nbsp;
          {dayjs(examContext?.exam_date).format("ddd, DD MMMM YYYY")}
          <CloseOutlined
            onClick={disableCloseOnLoading}
            style={{ color: "#fff", cursor: "pointer" }}
          />
        </div>
        <div style={{ padding: 30 }}>
          <div style={{ fontWeight: 500, fontSize: 20, marginBottom: 12 }}>
            {examContext?.exam_name}
          </div>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Controller
                name="subject"
                control={control}
                rules={{ required: true }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <CustomSelect
                    labelRowor="#171717"
                    placeholder="--Select--"
                    showSearch
                    size="large"
                    showRequired
                    label="Exam Subject"
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                      fetchLevels(e);
                      resetField("level", { defaultValue: "" });
                      resetField("start_time", { defaultValue: "" });
                      resetField("end_time", { defaultValue: "" });
                      setSections([]);
                      setRooms([]);
                      setTeachers([]);
                      setSectionsInput([]);
                    }}
                    status={error ? "error" : ""}
                    filterOption={(input, option) =>
                      option?.children
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {subjects?.map((option) => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </CustomSelect>
                )}
              />
            </Col>
            <Col span={12}>
              <Controller
                name="paper"
                control={control}
                rules={{ required: true }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <CustomSelect
                    labelRowor="#171717"
                    placeholder="--Select--"
                    showSearch
                    size="large"
                    showRequired
                    label="Select Paper"
                    value={value}
                    onChange={onChange}
                    status={error ? "error" : ""}
                    filterOption={(input, option) =>
                      option?.children
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {classRooms.map((option) => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </CustomSelect>
                )}
              />
            </Col>
            <Col span={12}>
              <Controller
                name="level"
                control={control}
                rules={{ required: true }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <CustomSelect
                    labelRowor="#171717"
                    placeholder="--Select--"
                    showSearch
                    size="large"
                    showRequired
                    label="Select Level"
                    disabled={!subject}
                    value={value}
                    onChange={(id) => {
                      onChange(id);
                      fetchSectionByLevels(id);
                    }}
                    status={error ? "error" : ""}
                    filterOption={(input, option) =>
                      option?.children
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {levels?.map((option) => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </CustomSelect>
                )}
              />
            </Col>
            <Col span={6}>
              <Controller
                name="start_time"
                control={control}
                rules={{ required: true }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <CustomTimePicker
                    onChange={onChange}
                    value={value}
                    label="Start Time"
                    size="large"
                    disabled={!level || !subject}
                    showRequired
                    format="hh:mm a"
                    style={{ width: "100%", height: 40 }}
                    status={error ? "error" : ""}
                    prefix={
                      <ClockCircleOutlined
                        style={{ color: "#999999", fontSize: 20 }}
                      />
                    }
                  />
                )}
              />
            </Col>
            <Col span={6}>
              <Controller
                name="end_time"
                control={control}
                rules={{ required: true }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <CustomTimePicker
                    onChange={onChange}
                    value={value}
                    label="End Time"
                    size="large"
                    format="hh:mm a"
                    disabled={!level || !subject}
                    showRequired
                    style={{ width: "100%", height: 40 }}
                    status={error ? "error" : ""}
                    prefix={
                      <ClockCircleOutlined
                        style={{ color: "#999999", fontSize: 20 }}
                      />
                    }
                  />
                )}
              />
            </Col>
            <Col span={24}>
              {sectionsInput?.length > 0 ? (
                <Row
                  gutter={24}
                  className={styles.sectionInputHeaders}
                >
                  {["", "Room No.", "Invigilator", "Marking Teacher"].map((label, index) => (
                    <Col key={index} span={6} className={styles["section-input-header"]}>
                      {label}
                    </Col>
                  ))}
                </Row>
              ) : null}
              {sectionsInput?.map((item, index) => {
                return (
                  <Row
                    gutter={24}
                    key={item.id}
                    className={styles.sectionInputRow}
                  >
                    <Col span={6}>
                      <CustomCheckBox
                        id="selected"
                        label={item?.name}
                        disabled={!startTime || !endTime}
                        checked={item.selected}
                        onChange={handleCheckbox(item?.id)}
                        className={styles.sectionCheckbox}
                      />
                    </Col>
                    <Col span={6}>
                      <CustomSelect
                        labelRowor="#171717"
                        placeholder="Select Room"
                        showSearch
                        size="large"
                        showRequired
                        value={item.selected_room}
                        onChange={handleChangeSections(
                          "selected_room",
                          item?.id
                        )}
                        disabled={!item.selected}
                        filterOption={(input, option) =>
                          option?.children
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                      >
                        {rooms?.map((option) => (
                          <Select.Option
                            key={option.value}
                            value={option.value}
                          >
                            {option.label}
                          </Select.Option>
                        ))}
                      </CustomSelect>
                    </Col>
                    <Col span={6}>
                      <CustomSelect
                        labelRowor="#171717"
                        placeholder="Select Invigilator"
                        showSearch
                        size="large"
                        showRequired
                        value={item.invigilator}
                        disabled={!item.selected}
                        onChange={handleChangeSections("invigilator", item?.id)}
                        filterOption={(input, option) =>
                          option?.children
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                      >
                        {teachers?.map((option) => (
                          <Select.Option
                            key={option.value}
                            value={option.value}
                          >
                            {option.label}
                          </Select.Option>
                        ))}
                      </CustomSelect>
                    </Col>
                    <Col span={6}>
                      <CustomSelect
                        labelRowor="#171717"
                        placeholder="Select Marking Teacher"
                        showSearch
                        size="large"
                        showRequired
                        value={item.marking_teacher}
                        disabled={!item.selected}
                        onChange={handleChangeSections("marking_teacher", item?.id)}
                        filterOption={(input, option) =>
                          option?.children
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                      >
                        {allTeachers?.map((option) => (
                          <Select.Option
                            key={option.value}
                            value={option.value}
                          >
                            {option.label}
                          </Select.Option>
                        ))}
                      </CustomSelect>
                    </Col>
                  </Row>
                );
              })}
            </Col>
            <Col span={8}>
              <Controller
                name="maximum_marks"
                control={control}
                rules={{ required: true }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <CustomTextfield
                    id="maximum_marks"
                    label="Maximum Marks"
                    showRequired
                    labelColor="#434343"
                    type="number"
                    placeholder="Enter Maximum Marks"
                    size="large"
                    value={value}
                    onChange={onChange}
                    status={error ? "error" : ""}
                  />
                )}
              />
            </Col>
            <Col span={8}>
              <Controller
                name="minimum_marks"
                rules={{ required: true }}
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <CustomTextfield
                    id="minimum_marks"
                    label="Minimum Marks"
                    labelColor="#434343"
                    type="number"
                    placeholder="Enter Minimum Marks"
                    size="large"
                    showRequired
                    value={value}
                    onChange={onChange}
                    status={error ? "error" : ""}
                  />
                )}
              />
            </Col>
            <Col span={8}>
              <Controller
                name="weightage"
                control={control}
                rules={{ required: true }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <CustomTextfield
                    id="weightage"
                    label="Weight"
                    labelColor="#434343"
                    type="number"
                    placeholder="Enter Weight"
                    size="large"
                    showRequired
                    value={value}
                    onChange={onChange}
                    status={error ? "error" : ""}
                  />
                )}
              />
            </Col>
          </Row>
        </div>
        <hr />
        <div style={{ paddingTop: 80 }}>
          <FormFooter
            handlePreviousButtonClick={disableCloseOnLoading}
            cancelButtonLabel="Cancel"
            saveButtonLabel="Save Exam"
            handleClickNextButton={handleSaveExam}
          />
        </div>
      </Spin>
    </Modal>
  );
};

export default AddEditExamModal;
